// -----------------------------------------------------------------------------
// src/CreatorUser/CreatorPages/Dashboard/creatorcomponents/CreatorCampaignPitchedDialog.jsx
// Mobile-responsive fix with proper iOS safe areas and app shell handling
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Stack,
  Chip,
  Card,
  Link,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import client from "../../../../API";
import { useCreatorAuth } from "../../../../Hooks/creator-use-auth";
import useAlertDialog from "../../../../Components/useAlertDialog";
import AlertDialog from "../../../../Components/AlertDialog";
import useIsDesktop from "../../../../Hooks/useIsDesktop";
import CreatorNegotiateDialog from "./creatorcounterDialog";
import CreatorCampaignConversation from "./conversation";

/* ───────── constants ───────── */
const APP_SHELL_HEIGHT = 86; // Must match BAR_HEIGHT from AppShell
const EXTRA_GUTTER = 12;

/* ───────── helpers ───────── */
const formatIdealDueDate = (dStr) => {
  const d = new Date(dStr);
  return isNaN(d)
    ? "Invalid Date"
    : `${String(d.getMonth() + 1).padStart(2, "0")}/` +
      `${String(d.getDate()).padStart(2, "0")}/` +
      `${d.getFullYear().toString().slice(-2)}`;
};

const renderBriefWithLinks = (txt = "") => {
  const url = /(https?:\/\/[^\s]+)/g;
  return txt.split(url).map((part, i) =>
    url.test(part) ? (
      <Link key={i} href={part} target="_blank" rel="noopener">
        {part}
      </Link>
    ) : (
      part
    )
  );
};

export default function CreatorCampaignPitchedDialog({
  openDialog,
  handleCloseDialog,
  campaignId,
}) {
  const [campaign, setCampaign] = useState(null);
  const [openNegotiateDialog, setOpenNegotiateDialog] = useState(false);
  const [openChatPopup, setOpenChatPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const editedEvents = useRef(false);

  const { creatorToken } = useCreatorAuth();
  const isDesktop = useIsDesktop();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { dialogState, openDialog: openAlert, closeDialog } = useAlertDialog();

  /* ── fetch ── */
  useEffect(() => {
    if (!openDialog) return;
    (async () => {
      try {
        const data = await client.campaigns.fetch(campaignId);
        editedEvents.current = false;
        setCampaign(data);
        setIsLoading(false);
      } catch (err) {
        console.error(err);
        handleCloseDialog();
      }
    })();
  }, [openDialog, campaignId, handleCloseDialog]);

  /* ── derived ── */
  const creator = useMemo(
    () =>
      campaign?.creators.find(
        (c) => c.id === creatorToken.creator_user.username
      ),
    [campaign, creatorToken.creator_user.username]
  );
  const currentRate = creator?.agencyRate || creator?.price || 0;
  const videoList = useMemo(
    () => creator?.videos.filter((v) => !v.parentId) || [],
    [creator]
  );
  const crosspostList = useMemo(
    () => creator?.videos.filter((v) => v.parentId) || [],
    [creator]
  );

  const refreshCampaign = async () => {
    try {
      const data = await client.campaigns.fetch(campaignId);
      setCampaign(data);
    } catch (err) {
      console.error("Error refreshing campaign:", err);
    }
  };

  /* ── accept / reject ── */
  const { mutate: updateStatus, isLoading: updatingStatus } = useMutation(
    (input) => client.creatorConnect.campaignUpdateStatus(campaignId, input),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "campaigns",
          "creator",
          creatorToken.creator_user.username,
        ]);
        handleCloseDialog();
        navigate("/creatorconnect/collabs", { replace: true });
      },
    }
  );

  const handleChangeStatus = (accepted) => {
    closeDialog();
    updateStatus(
      { status: accepted ? "Accepted" : "Declined" },
      {
        onSuccess: () =>
          openAlert(
            accepted ? "Accepted Campaign" : "Rejected Campaign",
            accepted
              ? "You have accepted to take part in this campaign. Good luck!"
              : "You have decided to reject this campaign. We hope to send campaigns more relevant to you in the future",
            closeDialog,
            null,
            "Ok"
          ),
        onError: (e) =>
          openAlert(
            "Error",
            `There was an error while changing your status: ${
              e.response?.data?.error || e.message
            }`,
            closeDialog,
            null,
            "Ok"
          ),
      }
    );
  };

  const onClickOption = (accepted) =>
    openAlert(
      accepted ? "Accept Campaign" : "Reject Campaign",
      accepted
        ? "You'll accept this campaign, be notified of upcoming due dates and receive messages from the campaign manager. Continue?"
        : "You'll reject this campaign. This action is not reversible, and the campaign will disappear from your dashboard. Continue?",
      () => handleChangeStatus(accepted),
      closeDialog,
      "Continue",
      "Cancel"
    );

  /* ── UI ── */
  return (
    <>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth={isLoading ? "sm" : "md"}
        fullWidth
        scroll="paper"
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            // Proper margins handling
            margin: isMobile ? 0 : theme.spacing(2),
            // iOS safe area handling
            paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : 0,
            // Full height handling with safe areas
            height: isMobile ? '100vh' : 'auto',
            maxHeight: isMobile ? '100vh' : '90vh',
            // Border radius
            borderRadius: isMobile ? 0 : theme.shape.borderRadius,
            // Display flex for proper layout
            display: 'flex',
            flexDirection: 'column',
          }
        }}
        PaperProps={{ 
          elevation: 1, 
          sx: { 
            boxShadow: isMobile ? 'none' : 24,
            // Ensure touch scrolling works properly on iOS
            WebkitOverflowScrolling: 'touch',
            // Flex layout for sticky header/footer
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '100%',
          } 
        }}
      >
        {(isLoading || updatingStatus) ? (
          <DialogContent
            sx={{ 
              display: "flex", 
              justifyContent: "center", 
              alignItems: "center",
              flex: 1,
              py: 6,
              minHeight: isMobile ? '200px' : 'auto',
            }}
          >
            <CircularProgress />
          </DialogContent>
        ) : (
          <>
            {/* Sticky Header */}
            <Paper 
              square 
              elevation={isMobile ? 0 : 2}
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                borderRadius: 0,
              }}
            >
              <DialogTitle
                sx={{
                  fontSize: isMobile ? '1.25rem' : '1.5rem',
                  py: isMobile ? 2 : 3,
                  borderBottom: isMobile ? `1px solid ${theme.palette.divider}` : 'none',
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                Accept this campaign?
              </DialogTitle>
            </Paper>

            {/* Scrollable Content */}
            <DialogContent 
              dividers={!isMobile}
              sx={{
                px: isMobile ? 2 : 3,
                py: isMobile ? 2 : 3,
                flex: 1,
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                // Ensure content doesn't get cut off
                minHeight: 0,
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  fontSize: isMobile ? '1.25rem' : '1.5rem',
                  fontWeight: 'bold',
                  mb: 2,
                }}
              >
                Campaign Name: {campaign.name}
              </Typography>

              <Typography 
                variant="h6" 
                sx={{ 
                  mt: 2,
                  fontSize: isMobile ? '1.1rem' : '1.25rem',
                  fontWeight: 'semibold',
                }}
              >
                Campaign Brief:
              </Typography>
              <Typography 
                sx={{ 
                  whiteSpace: "pre-wrap",
                  mb: 2,
                  lineHeight: 1.6,
                  fontSize: isMobile ? '0.9rem' : '1rem',
                }}
              >
                {renderBriefWithLinks(campaign.brief)}
              </Typography>

              <Typography 
                variant="h6" 
                sx={{ 
                  mt: 2,
                  fontSize: isMobile ? '1.1rem' : '1.25rem',
                  fontWeight: 'semibold',
                }}
              >
                Ideal due date: {formatIdealDueDate(campaign.ideal_duedate)}
              </Typography>
              
              <Divider sx={{ my: 2 }} />

              {/* Promotions */}
              <Box sx={{ mt: 2 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 1,
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    fontWeight: 'semibold',
                  }}
                >
                  Promotions (x{videoList.length}):
                </Typography>
                <Stack 
                  direction={isMobile ? "column" : "row"} 
                  spacing={1} 
                  sx={{ mb: 2 }}
                >
                  {videoList.map((v) => (
                    <Chip
                      key={v.id}
                      label={`${v.promotionPlatform} - ${v.promotionType}`}
                      color="primary"
                      sx={{
                        height: isMobile ? 36 : 32,
                        fontSize: isMobile ? '0.85rem' : '0.8125rem',
                        alignSelf: isMobile ? 'flex-start' : 'auto',
                      }}
                    />
                  ))}
                </Stack>

                {/* Crossposts */}
                <Typography 
                  variant="subtitle2"
                  sx={{ 
                    mb: 1,
                    fontSize: isMobile ? '0.9rem' : '1rem',
                    fontWeight: 'semibold',
                  }}
                >
                  Crossposts:
                </Typography>
                <Stack 
                  direction={isMobile ? "column" : "row"} 
                  spacing={1}
                  sx={{ mb: 2 }}
                >
                  {crosspostList.map((v) => (
                    <Chip
                      key={v.id}
                      label={v.promotionPlatform}
                      color="secondary"
                      sx={{
                        height: isMobile ? 36 : 32,
                        fontSize: isMobile ? '0.85rem' : '0.8125rem',
                        alignSelf: isMobile ? 'flex-start' : 'auto',
                      }}
                    />
                  ))}
                </Stack>

                {/* Creator brief */}
                <Box sx={{ mt: 2 }}>
                  <Typography
                    sx={{
                      fontSize: isMobile ? '0.9rem' : '1rem',
                      fontWeight: 'semibold',
                      mb: 1,
                    }}
                  >
                    Creator Brief
                  </Typography>
                  <Card 
                    variant="outlined" 
                    sx={{ 
                      p: isMobile ? 1.5 : 2,
                      backgroundColor: theme.palette.grey[50],
                    }}
                  >
                    <Typography 
                      sx={{ 
                        whiteSpace: "pre-wrap",
                        fontSize: isMobile ? '0.85rem' : '0.9rem',
                        lineHeight: 1.5,
                      }}
                    >
                      {renderBriefWithLinks(creator?.creatorBrief)}
                    </Typography>
                  </Card>
                </Box>
              </Box>

              {/* Add extra bottom padding to prevent content from being cut off by actions */}
              <Box sx={{ height: isMobile ? 16 : 8 }} />
            </DialogContent>

            {/* Sticky Footer with Actions */}
            <Paper 
              square 
              elevation={isMobile ? 0 : 2}
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 1,
                borderTop: isMobile ? `1px solid ${theme.palette.divider}` : 'none',
                borderRadius: 0,
                // Account for iOS bottom safe area AND app shell
                paddingBottom: isMobile 
                  ? `max(${APP_SHELL_HEIGHT + EXTRA_GUTTER}px, calc(env(safe-area-inset-bottom, 0px) + ${APP_SHELL_HEIGHT + EXTRA_GUTTER}px))`
                  : 0,
              }}
            >
              <DialogActions
                sx={{
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 1 : 0.5,
                  p: isMobile ? 2 : 1.5,
                  justifyContent: isMobile ? 'stretch' : 'flex-end',
                  minHeight: isMobile ? 'auto' : 52,
                  backgroundColor: theme.palette.background.paper,
                }}
              >
                {/* Primary actions row */}
                <Stack
                  direction={isMobile ? "column" : "row"}
                  spacing={isMobile ? 1 : 1}
                  sx={{
                    width: isMobile ? '100%' : 'auto',
                    order: isMobile ? 1 : 0,
                  }}
                >
                  {creator?.status !== "Counter" && (
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => onClickOption(true)}
                      sx={{
                        minHeight: 44,
                        fontSize: isMobile ? '1rem' : '0.875rem',
                        fontWeight: 'semibold',
                        px: isMobile ? 3 : 2,
                        py: isMobile ? 1.5 : 1,
                        width: isMobile ? '100%' : 'auto',
                      }}
                    >
                      Accept
                    </Button>
                  )}

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setOpenChatPopup(true)}
                    sx={{
                      minHeight: 44,
                      fontSize: isMobile ? '1rem' : '0.875rem',
                      fontWeight: 'semibold',
                      px: isMobile ? 3 : 2,
                      py: isMobile ? 1.5 : 1,
                      width: isMobile ? '100%' : 'auto',
                    }}
                  >
                    Chat with Brand
                  </Button>
                </Stack>

                {/* Secondary actions row */}
                <Stack
                  direction={isMobile ? "column" : "row"}
                  spacing={isMobile ? 1 : 1}
                  sx={{
                    width: isMobile ? '100%' : 'auto',
                    order: isMobile ? 2 : 1,
                  }}
                >
                  {creator?.status !== "Counter" && (
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => {
                        setOpenNegotiateDialog(true);
                        navigate("/creatorconnect/collabs", { replace: true });
                      }}
                      sx={{
                        minHeight: 44,
                        fontSize: isMobile ? '1rem' : '0.875rem',
                        fontWeight: 'semibold',
                        px: isMobile ? 3 : 2,
                        py: isMobile ? 1.5 : 1,
                        width: isMobile ? '100%' : 'auto',
                      }}
                    >
                      Negotiate
                    </Button>
                  )}

                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => onClickOption(false)}
                    sx={{
                      minHeight: 44,
                      fontSize: isMobile ? '1rem' : '0.875rem',
                      fontWeight: 'semibold',
                      px: isMobile ? 3 : 2,
                      py: isMobile ? 1.5 : 1,
                      width: isMobile ? '100%' : 'auto',
                      borderWidth: 2,
                      '&:hover': {
                        borderWidth: 2,
                      },
                    }}
                  >
                    Reject
                  </Button>
                </Stack>
              </DialogActions>
            </Paper>
          </>
        )}
      </Dialog>

      {/* Negotiate Dialog */}
      <CreatorNegotiateDialog
        open={openNegotiateDialog}
        handleClose={() => setOpenNegotiateDialog(false)}
        campaignId={campaignId}
        currentRate={currentRate}
        refreshCampaign={refreshCampaign}
        onSuccess={handleCloseDialog}
      />

      {/* Chat Dialog - Also improved for mobile */}
      <Dialog
        open={openChatPopup}
        onClose={() => setOpenChatPopup(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            height: isMobile ? '100vh' : '70vh',
            margin: isMobile ? 0 : theme.spacing(2),
            borderRadius: isMobile ? 0 : theme.shape.borderRadius,
            paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : 0,
            display: 'flex',
            flexDirection: 'column',
          }
        }}
        PaperProps={{ 
          sx: { 
            boxShadow: isMobile ? 'none' : 24,
            WebkitOverflowScrolling: 'touch',
            display: 'flex',
            flexDirection: 'column',
          } 
        }}
      >
        <Paper 
          square 
          elevation={0}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <DialogTitle
            sx={{
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              py: isMobile ? 2 : 3,
              borderBottom: isMobile ? `1px solid ${theme.palette.divider}` : 'none',
              backgroundColor: theme.palette.background.paper,
            }}
          >
            Chat with Brand
          </DialogTitle>
        </Paper>
        
        <DialogContent 
          dividers={!isMobile}
          sx={{ 
            p: 0, 
            display: "flex", 
            flexDirection: "column",
            flex: 1,
            WebkitOverflowScrolling: 'touch',
            minHeight: 0,
          }}
        >
          <CreatorCampaignConversation campaignId={campaignId} />
        </DialogContent>
        
        <Paper 
          square 
          elevation={0}
          sx={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            borderTop: isMobile ? `1px solid ${theme.palette.divider}` : 'none',
            paddingBottom: isMobile 
              ? `max(${APP_SHELL_HEIGHT + EXTRA_GUTTER}px, calc(env(safe-area-inset-bottom, 0px) + ${APP_SHELL_HEIGHT + EXTRA_GUTTER}px))`
              : 0,
          }}
        >
          <DialogActions
            sx={{
              p: isMobile ? 2 : 1.5,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Button 
              onClick={() => setOpenChatPopup(false)}
              sx={{
                minHeight: 44,
                fontSize: isMobile ? '1rem' : '0.875rem',
                px: isMobile ? 3 : 2,
                py: isMobile ? 1.5 : 1,
                width: isMobile ? '100%' : 'auto',
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Paper>
      </Dialog>

      <AlertDialog alertState={dialogState} />
    </>
  );
}